import React, { useEffect, useState, useReducer, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { OrderedMap } from 'immutable';
import { Motion, spring } from 'react-motion';

import { makeStyles } from '@material-ui/core/styles';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import Fade from '@material-ui/core/Fade';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DialogActions from '@material-ui/core/DialogActions';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';

import StdDialog from 'components/Dialogs/NewStdDialog';
import { importData } from 'data/import/importActions';
import { mkDocument } from 'data/documents/types';
import { csvImportFilesInProgress, csvImportFilesSucceed, csvImportFilesFailed } from 'data/app/selectors'
import LoadingView from 'components/LoadingView';
import QButton, { QButtonVariantEnum } from 'components/QButton';

const ViewStateEnum = Object.freeze({
  IMPORT_FROM: 'IMPORT_FROM',
  MINT_INSTRUCTIONS: 'MINT_INSTRUCTIONS',
  OTHER_INSTRUCTIONS: 'OTHER_INSTRUCTIONS',
  FILE_PROCESSING: 'FILE_PROCESSING',
  FILE_SUCCEED: 'FILE_SUCCEED',
  FILE_PARTIALLY_SUCCEED: 'FILE_PARTIALLY_SUCCEED',
  FILE_FAILED: 'FILE_FAILED',
});

const contentStyle = {
  width: '100%',
  height: '100%',
};

const useStyles = makeStyles((theme) =>({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  },
  contentWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  contentPlaceholder: {
    ...contentStyle,
    position: 'static',
    visibility: 'hidden',
  },
  content: {
    ...contentStyle,
    position: 'absolute',
    top: 0,
    transition: 'all 300ms',
  },
  otherInstructions: {
    maxWidth: 810,
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 40,
  },
  statusImage: {
    width: 128,
    height: 128,
    marginTop: 24,
    marginBottom: 24,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  listItem: {
    display: 'flex',
    alignItems: 'baseline',
    marginTop: 20,
    marginBottom: 20,
  },
  step: {
    minWidth: 60,
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
  },
  header: {
    backgroundColor: theme.palette.action.disabledBackground,
    border: `1px solid ${theme.palette.background.default}`,
    padding: 3,
  },
  cell: {
    padding: 3,
  },
  dropZonePlaceholder: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.default,
    opacity: 0.9,
    display: 'flex',
  },
  dropZoneContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    border: `4px dashed ${theme.palette.action.selected}`,
    borderRadius: 8,
    margin: 30,
  },
  option: {
    paddingLeft: 0,
  },
}));

const FileImportDialog = (props) => {
  const { account, onHandleClose, open } = props;
  const accountId = account ? account.id : undefined;

  const classes = useStyles();

  const dispatch = useDispatch();
  const dropzone = useDropzone();
  const { acceptedFiles, getRootProps, open: dropzoneOpen, getInputProps, isDragActive, isFileDialogActive } = dropzone;
  const { onClick, ...dropzoneRootProps } = getRootProps();
  const [files, setFiles] = useState(OrderedMap());
  const [animateClose, setAnimateClose] = useState(false);

  useEffect(() => {
    if (acceptedFiles && dispatch) {
      acceptedFiles.forEach((file) => {
        const clientId = uuidv4();
        setFiles((filesMap) => filesMap.set(clientId, file));
        dispatch(importData({
          clientId,
          accountId,
          document: mkDocument({
            name: file.name,
            documentType: 'OTHER',
            contentType: file.type || 'text/csv',
            clientId: uuidv4(),
            size: file.size,
          }),
          data: file,
        }));
      });
    }
  }, [acceptedFiles, dispatch, accountId]);

  const filesInProgress = useSelector((state) => csvImportFilesInProgress(state, files));
  const filesComplete = useSelector((state) => csvImportFilesSucceed(state, files));
  const filesFailed = useSelector((state) => csvImportFilesFailed(state, files));

  const [errors, setErrors] = useState([]);
  useEffect(() => {
    const newErrors = [];
    filesComplete.forEach((file) => file.data?.errors?.forEach?.((error) => {
      newErrors.push(
        <Typography display="block" paragraph>
          <Typography variant="body" display="block">
            {`${newErrors.length + 1}. ${error.message}`}
          </Typography>
          <Typography variant="body2" display="block">
            Row: {error.recordNumber}&nbsp;&nbsp;&nbsp;
            Column: {error.columnName}&nbsp;&nbsp;&nbsp;
            Value: <Box bgcolor="warning.light" display="inline">{error.value}</Box>
          </Typography>
          <Typography variant="body2" display="block">
            [{error.row?.map?.((value, index) => (
              <>
                <Box bgcolor={error.columnIndex === index ? 'warning.light' : undefined} display="inline">
                  {value}
                </Box>
                {index !== (error.row.length - 1) && ', '}
              </>
            ))}]
          </Typography>
        </Typography>
      );
    }));
    setErrors(newErrors);
  }, [filesComplete]);

  const onHandleCloseOverride = useCallback((event, reason) => {
    if (onHandleClose) {
      onHandleClose(event, reason);
    }
  }, [onHandleClose]);

  const [viewState, dispatchViewStateAction] = useReducer((currentViewState, action) => {
    let newViewState = currentViewState;
    switch (action) {
      case 'importFrom':
        newViewState = {
          previousView: currentViewState.currentView,
          previousLeft: '+200%',
          currentView: ViewStateEnum.IMPORT_FROM,
        };
        break;
      case 'mintInstructions':
        newViewState = {
          previousView: currentViewState.currentView,
          previousLeft: '-100%',
          currentView: ViewStateEnum.MINT_INSTRUCTIONS,
        };
        break;
      case 'otherInstructions':
        newViewState = {
          previousView: currentViewState.currentView,
          previousLeft: '-100%',
          currentView: ViewStateEnum.OTHER_INSTRUCTIONS,
        };
        break;
      case 'updateFileState':
        if (filesInProgress.size) {
          newViewState = {
            previousView: undefined,
            currentView: ViewStateEnum.FILE_PROCESSING,
          };
        } else if (filesFailed.size) {
          newViewState = {
            previousView: currentViewState.currentView,
            currentView: ViewStateEnum.FILE_FAILED,
          };
        } else if (filesComplete.size) {
          if (errors?.length) {
            newViewState = {
              previousView: currentViewState.currentView,
              currentView: ViewStateEnum.FILE_PARTIALLY_SUCCEED,
            };
          } else {
            newViewState = {
              previousView: currentViewState.currentView,
              currentView: ViewStateEnum.FILE_SUCCEED,
            };
            setTimeout(() => {
              setAnimateClose(true);
              onHandleCloseOverride(undefined, 'auto');
            }, 3000);
          }
        }
        break;
      case 'tryAgain':
        newViewState = {
          previousView: currentViewState.currentView,
          currentView: ViewStateEnum.IMPORT_FROM,
        };
        break;
      default:
        assert(false, `'${action}' - unknown action`);
        newViewState = {
          previousView: currentViewState.currentView,
          currentView: ViewStateEnum.IMPORT_FROM,
        };
    }
    return newViewState;
  }, {
    previousView: undefined,
    previousLeft: undefined,
    currentView: ViewStateEnum.IMPORT_FROM,
  });

  useEffect(() => {
    if (open) {
      setAnimateClose(false);
      setFiles(OrderedMap());
      dispatchViewStateAction('importFrom');
    }
  }, [open]);

  useEffect(() => dispatchViewStateAction('updateFileState'), [filesInProgress, filesComplete, filesFailed]);

  const importFromPage = useMemo(() => (
    <>
      <Typography variant="body2">
        Import Transactions into any banking or credit card account in Simplifi with ease.
      </Typography>

      <List component="nav" aria-label="main mailbox folders">
        <ListItem
          className={classes.option}
          button
          onClick={() => dispatchViewStateAction('mintInstructions')}
        >
          <ListItemText primary="Importing from Mint"/>
          <NavigateNextIcon/>
        </ListItem>
        <Divider/>
        <ListItem
          className={classes.option}
          button
          onClick={() => dispatchViewStateAction('otherInstructions')}
          disabled
        >
          <ListItemText>
            Importing from Other
            &nbsp;&nbsp;
            <Chip color="primary" size="small" label="coming soon"/>
          </ListItemText>
          <NavigateNextIcon/>
        </ListItem>
      </List>
    </>
  ), [classes.option, dispatchViewStateAction]);

  const mintInstructionsPage = useMemo(() => (
    <>
      <Typography variant="subtitle1">
        Importing from Mint
      </Typography>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 1
        </Typography>

        <Typography variant="body2">
          {`Go to the transaction page for ${account.name} in Mint.`}
        </Typography>
      </div>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 2
        </Typography>
        <Typography variant="body2">
          Scroll to the bottom of transaction list and click Export all transactions.
        </Typography>
      </div>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 3
        </Typography>
        <Typography variant="body2" component="div">
          {'On Simplifi, in this dialog, click "Import" and select your file you exported from Mint '}
          <Typography variant="subtitle2">
            or Drag & Drop your file here.
          </Typography>
        </Typography>
      </div>
    </>
  ), [account.name, classes.listItem, classes.step]);

  const otherInstructionsPage = useMemo(() => (
    <div className={classes.otherInstructions}>
      <Typography variant="subtitle1">
        Importing from any CSV
      </Typography>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 1
        </Typography>

        <div>
          <Typography variant="body2">
            Create a spreadsheet using these exact column headers
          </Typography>

          <Table size="small" padding="none" className={classes.table}>
            <TableHead>
              <TableRow>
                {["Date", "Description", "Original Description", "Amount", "Transaction Type", "Category", "Account Name", "Labels", "Notes"].map((label) => (
                  <TableCell align="center" className={classes.header} key={label}>
                    <Typography variant="caption">
                      {label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[["3/13/2020", "Starbucks", "STARBUCKS COFFEE", "7.00", "debit", "Coffee Shops", "Primary Checking", "Vacation", ""],
                ["3/15/2020", "Uber", "UBER *TRIP", "21.12", "debit", "Rental Car & Taxi", "Primary Checking", "Hawaii Vacation", "Split it"],
                ["3/17/2020", "Acme", "ACME PAYCHECK", "1735.21", "credit", "Income", "Primary Checking", "", ""],
              ].map((row) => (
                <TableRow key={row[1]}>
                  {row.map((cell, index) => (
                    <TableCell
                      align="left"
                      component={index ? undefined : 'th'}
                      scope={index ? undefined : 'row'}
                      className={classes.cell}
                      key={`${index}:${cell}`} // eslint-disable-line react/no-array-index-key
                    >
                      <Typography variant="caption">
                        {cell}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="body2" component="div">
            or,&nbsp;&nbsp;<Link href="files/import template.csv">Download Template</Link>
          </Typography>
        </div>

      </div>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 2
        </Typography>
        <Typography variant="body2">
          {"Enter or paste your transactions into your spreadsheet. "
          + "Please note, you can only add transactions from one account at a time into Simplifi. "
          + "Make sure your spreadsheet uses the exact same column headers that we've provided."}
        </Typography>
      </div>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 3
        </Typography>
        <Typography variant="body2">
          Save your spreadsheet as a .csv
        </Typography>
      </div>

      <div className={classes.listItem}>
        <Typography variant="subtitle2" className={classes.step}>
          Step 4
        </Typography>
        <Typography variant="body2" component="div">
          {'Click “Import” and select your spreadsheet, '}
          <Typography variant="subtitle2" display="inline">
            or Drag & Drop your file here.
          </Typography>
        </Typography>
      </div>
    </div>
  ), [classes.cell, classes.header, classes.listItem, classes.otherInstructions, classes.step, classes.table]);

  return (
    <StdDialog
      title={
        <>
          Import Transactions
          <Typography variant="subtitle2" component="div">
            to {account.name}
          </Typography>
        </>
      }
      dialogProps={{
        maxWidth: 'md',
        transitionDuration: animateClose ? 3000 : undefined,
        ...(isFileDialogActive ? {} : dropzoneRootProps),
      }}
      {...props}
      onHandleClose={onHandleCloseOverride}
    >
      <>
        <DialogContent className={classes.container}>
          <div className={classes.contentWrapper}>

            {viewState.currentView === ViewStateEnum.IMPORT_FROM &&
            <div className={classes.contentPlaceholder}>
              {importFromPage}
            </div>
            }
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.IMPORT_FROM ? 'visible' : 'hidden',
                left: viewState.currentView === ViewStateEnum.IMPORT_FROM ? 0 : viewState.previousView === ViewStateEnum.IMPORT_FROM && viewState.previousLeft || '+200%',
              }}
            >
              {importFromPage}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS &&
            <div className={classes.contentPlaceholder}>
              {mintInstructionsPage}
            </div>
            }
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS ? 'visible' : 'hidden',
                left: viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS ? 0 : viewState.previousView === ViewStateEnum.MINT_INSTRUCTIONS && viewState.previousLeft || '+200%',
              }}
            >
              {mintInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS &&
            <div className={classes.contentPlaceholder}>
              {otherInstructionsPage}
            </div>
            }
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS ? 'visible' : 'hidden',
                left: viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS ? 0 : viewState.previousView === ViewStateEnum.OTHER_INSTRUCTIONS && viewState.previousLeft || '+200%',
              }}
            >
              {otherInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.FILE_PROCESSING &&
            <Fade in mountOnEnter unmountOnExit>
              <LoadingView
                title="Importing CSV"
                subTitle={filesInProgress.toIndexedSeq().map((file) => file.name).join(', ')}
                size={128}
                style={{margin: 40}}
              />
            </Fade>
            }

            {viewState.currentView === ViewStateEnum.FILE_SUCCEED &&
            <Fade in mountOnEnter unmountOnExit>
              <div className={classes.statusContainer}>
                <CheckCircleOutlineIcon
                  color="primary"
                  className={classes.statusImage}
                />
                <Typography variant="h6">
                  CSV Uploaded successfully
                </Typography>
                <Typography variant="subtitle2">
                  {filesComplete.keySeq().filter((clientId) => files.get(clientId)).map((clientId) => files.get(clientId).name).join(', ')}
                </Typography>
              </div>
            </Fade>
            }

            {viewState.currentView === ViewStateEnum.FILE_PARTIALLY_SUCCEED &&
            <Fade in mountOnEnter unmountOnExit>
              <div className={classes.statusContainer}>
                <CancelOutlinedIcon
                  color="error"
                  className={classes.statusImage}
                />
                <Typography variant="h6">
                  Uh oh! The file you submitted has errors.
                </Typography>
                <Typography variant="subtitle2">
                  {"You can fix the errors and re-upload or, if the errors don’t matter to you, you can continue as is."}
                </Typography>
                <Box
                  maxHeight={200}
                  overflow="scroll"
                  border={1}
                  borderRadius={4}
                  borderColor="grey.A200"
                  width="100%"
                  p={2}
                  mt={3}
                  mb={3}
                >
                  {errors.map((error) => error)}
                </Box>
                <QButton
                  variant={QButtonVariantEnum.CONTAINED}
                  onClick={() => {
                    setFiles(OrderedMap());
                    dispatchViewStateAction('tryAgain');
                  }}
                >
                  Fix & Upload Again
                </QButton>
                <Box height={14}/>
                <Button onClick={onHandleCloseOverride}>
                  Continue As Is
                </Button>
              </div>
            </Fade>
            }

            {viewState.currentView === ViewStateEnum.FILE_FAILED &&
            <Fade in mountOnEnter unmountOnExit>
              <div className={classes.statusContainer}>
                <CancelOutlinedIcon
                  color="error"
                  className={classes.statusImage}
                />
                <Typography variant="h6">
                  Unable to import
                </Typography>
                <Typography variant="subtitle2">
                  {filesFailed.keySeq().filter((clientId) => files.get(clientId)).map((clientId) => files.get(clientId).name).join(', ')}
                </Typography>
                <br/>
                <QButton
                  variant={QButtonVariantEnum.CONTAINED}
                  onClick={() => {
                    setFiles(OrderedMap());
                    dispatchViewStateAction('tryAgain');
                  }}
                >
                  TRY AGAIN
                </QButton>
              </div>
            </Fade>
            }

          </div>
        </DialogContent>

        {(viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS || viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS) &&
        <DialogActions>
          <QButton
            variant={QButtonVariantEnum.OUTLINED}
            onClick={() => dispatchViewStateAction('importFrom')}
          >
            GO BACK
          </QButton>

          <QButton
            variant={QButtonVariantEnum.CONTAINED}
            onClick={() => dropzoneOpen()}
          >
            <CloudUploadIcon/>
            &nbsp;
            IMPORT
          </QButton>
        </DialogActions>
        }

        <input {...getInputProps()} />

        {isDragActive &&
        <div className={classes.dropZonePlaceholder}>
          <div className={classes.dropZoneContent}>
            <Motion
              defaultStyle={{ delta: -20 }}
              style={{
                delta: spring(0, {
                  stiffness: 100,
                  damping: 3,
                  precision: 1,
                }),
              }}
            >
              {({ delta }) =>
                <div style={{ position: 'relative', top: delta }}>
                  <CloudUploadIcon className={classes.statusImage}/>
                  Drag & Drop to upload
                </div>
              }
            </Motion>
          </div>
        </div>
        }
      </>
    </StdDialog>
  );
};

FileImportDialog.propTypes = {
  open: PropTypes.bool,
  onHandleClose: PropTypes.func,
  account: PropTypes.object.isRequired,
};

export default FileImportDialog;
